/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect } from 'react';
import {
    bool, func, instanceOf, object, string,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import GuidedNavModalView from './GuidedNavModalView';
import EdsGuideNavModalView from './EdsGuideNavModalView';
import GfAndGnavAbTestContext from '../../../../../GraphqlCoreBrowsing/GfAndGnavAbTestContext';
import { getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import useClickStreamCustomEventsTracking from '../../../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getBrand } from '../../../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';

const GuideNavModal = ({
    currentAddress,
    handleButtonSubmit,
    guidedNavFormData,
    showQasModel,
    handleLocationFormChange,
    handleAddressChange,
    addressFieldOnFocus,
    addressFieldOnBlur,
    handleKeyDown,
    qasSuggestions,
    onSubmitCheck,
    isOpen,
    closeModal,
    getAddressInformation,
    hideQasModel,
    zipValid,
    selectedLocationType,
    loadGoogleMaps,
    trackEvent,
    zipDerivedFromGoogleMaps,
    activeABTests,
    guidedNavModalIsOpen,
    guidedNavModalEdsActivated,
    removeAriaAttribute,
}) => {
    const brand = useSelector(getBrand);
    const isGnavEdsEnabled = useSelector(getFeatureFlag('is-gnav-eds-enabled'));
    const gnavAbTestContext = useContext(GfAndGnavAbTestContext);
    // GNavPopUp SalesForce test setup
    const { loading: loadingMessaging, variables: variablesMessaging, data: gNavPopUp } = useSalesforcePersonalizedContentQuery({
        queryName: 'gNavPopUp',
        interactionName: `${brand.domain} - Get Campaign - Gnav Popup`,
        featureFlag: useSelector(getFeatureFlag('is-gnav-popup-ab-test-enabled')),
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: gNavPopUp?.contentSF,
        isFireImpression: true,
        page: 'collection',
    });

    // CONTROL OR VARIANT(TEST) VALUE for Guide Nav Popup Sales Force Test
    const isGNavPopUpsUserGroup = gNavPopUp?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const variantGNavPopUp = isGNavPopUpsUserGroup?.toLowerCase() !== 'test';

    useEffect(() => {
        if (typeof isGNavPopUpsUserGroup !== 'undefined' && typeof window !== 'undefined' && isGNavPopUpsUserGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Guided Nav Modal Test',
                eventLabel: variantGNavPopUp ? 'Control' : 'Variant',
                feature_detail: variantGNavPopUp ? 'Control' : 'Variant',
                feature_element: 'Guided Nav Modal Test',
                feature_category: 'Experiment',
            };
            trackEvent(eventParam);
        }
        if (!variantGNavPopUp) {
            closeModal();
        }
    }, [isGNavPopUpsUserGroup]);

    if (loadingMessaging || variablesMessaging?.skip) {
        return <></>;
    }

    if ((isGnavEdsEnabled || gnavAbTestContext?.gfAndGnavEdsAbTestVariant) && guidedNavModalEdsActivated && variantGNavPopUp) {
        return (
            <EdsGuideNavModalView
                guidedNavFormData={guidedNavFormData}
                isOpen={isOpen}
                handleAddressChange={handleAddressChange}
                handleLocationFormChange={handleLocationFormChange}
                currentAddress={currentAddress}
                getAddressInformation={getAddressInformation}
                addressFieldOnFocus={addressFieldOnFocus}
                handleButtonSubmit={handleButtonSubmit}
                closeModal={closeModal}
                zipValid={zipValid}
                onSubmitCheck={onSubmitCheck}
                qasSuggestions={qasSuggestions}
                showQasModel={showQasModel}
                hideQasModel={hideQasModel}
                handleKeyDown={handleKeyDown}
                addressFieldOnBlur={addressFieldOnBlur}
            />
        );
    }
    return (
        guidedNavModalIsOpen && (activeABTests?.loaded || activeABTests?.timeout) && (
            <GuidedNavModalView
                guidedNavFormData={guidedNavFormData}
                isOpen={isOpen}
                handleAddressChange={handleAddressChange}
                selectedLocationType={selectedLocationType}
                handleLocationFormChange={handleLocationFormChange}
                currentAddress={currentAddress}
                getAddressInformation={getAddressInformation}
                loadGoogleMaps={loadGoogleMaps}
                addressFieldOnFocus={addressFieldOnFocus}
                handleButtonSubmit={handleButtonSubmit}
                closeModal={closeModal}
                zipValid={zipValid}
                onSubmitCheck={onSubmitCheck}
                trackEvent={trackEvent}
                zipDerivedFromGoogleMaps={zipDerivedFromGoogleMaps}
                qasSuggestions={qasSuggestions}
                showQasModel={showQasModel}
                hideQasModel={hideQasModel}
                handleKeyDown={handleKeyDown}
                variantGNavPopUp={variantGNavPopUp}
                removeAriaAttribute={removeAriaAttribute}
            />

        )
    );
};
GuideNavModal.propTypes = {
    guidedNavFormData: object.isRequired,
    currentAddress: string.isRequired,
    isOpen: bool.isRequired,
    handleAddressChange: func.isRequired,
    addressFieldOnBlur: string.isRequired,
    handleLocationFormChange: func.isRequired,
    getAddressInformation: func.isRequired,
    addressFieldOnFocus: func.isRequired,
    handleButtonSubmit: func.isRequired,
    onSubmitCheck: bool.isRequired,
    closeModal: func.isRequired,
    qasSuggestions: object,
    showQasModel: bool,
    hideQasModel: func,
    handleKeyDown: func.isRequired,
    zipValid: bool.isRequired,
    selectedLocationType: string.isRequired,
    trackEvent: func.isRequired,
    loadGoogleMaps: bool.isRequired,
    zipDerivedFromGoogleMaps: string.isRequired,
    activeABTests: object.isRequired,
    guidedNavModalIsOpen: bool,
    guidedNavModalEdsActivated: bool,
    removeAriaAttribute: func.isRequired,
};

GuideNavModal.defaultProps = {
    qasSuggestions: {},
    showQasModel: false,
    hideQasModel: () => { },
    guidedNavModalIsOpen: false,
    guidedNavModalEdsActivated: false,
};

export default GuideNavModal;
